import { Card, CardContent } from '@crowdfpl/ui-web/card';
import { Separator } from '@crowdfpl/ui-web/separator';
import { Skeleton } from '@crowdfpl/ui-web/skeleton';
import { ArrowRight, Crown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DuelCard } from '@/components/duel-card';
import { trackUmamiEvent } from '@/helpers/analytics';
import type { UiDuel } from '@/hooks/data/duels';
import { useIsMobile } from '@/hooks/utils/use-mobile';

interface HeroSectionProps {
  gameweekNumber?: number;
  isLiveGameweek?: boolean;
  topDuel: UiDuel | null;
  duelsLoading: boolean;
  onVote: (duelId: string, side: string) => void;
  isAuthenticated: boolean;
  isVotingByDuelId: Record<string, boolean | undefined>;
  /** @deprecated Link is used internally now */
  onCTAClick?: () => void;
}

export function HeroSection({
  gameweekNumber,
  isLiveGameweek = false,
  topDuel,
  duelsLoading,
  onVote,
  isAuthenticated,
  isVotingByDuelId,
}: HeroSectionProps) {
  const isMobile = useIsMobile();

  return (
    <>
      {/* Desktop Hero */}
      {!isMobile ? (
        <div className="relative overflow-hidden rounded-3xl mx-4 mt-4 mb-8">
          <div className="absolute inset-0 bg-gradient-to-br from-background via-secondary/30 to-primary/10" />
          <div className="relative grid md:grid-cols-2 gap-8 md:gap-12 p-8 md:p-12 lg:px-20 lg:py-12">
            {/* Left Section */}
            <div className="flex flex-col justify-center space-y-6 md:space-y-8">
              {/* Gameweek Label */}
              {gameweekNumber && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full w-fit">
                  {isLiveGameweek && (
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                  )}
                  <span className="text-sm font-medium text-primary">Live for GW{gameweekNumber}</span>
                </div>
              )}

              {/* Main Headline */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Pick the right player,{' '}
                <span className="text-primary">every Gameweek</span>
              </h1>

              {/* Subheadline */}
              <p className="text-lg md:text-xl text-muted-foreground max-w-md">
                Trust the crowd, lead the crowd
              </p>

              {/* CTA Button */}
              <Link
                to="/duels"
                onClick={() => trackUmamiEvent('hero_all_duels_click', { isMobile: false })}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium bg-primary text-primary-foreground shadow hover:bg-primary/90 h-11 px-8 w-fit group"
              >
                All Duels
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Right Section - Top Duel */}
            <div className="flex items-center justify-center">
              {duelsLoading ? (
                <Card className="w-full max-w-md shadow-sm">
                  <CardContent className="p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-5 w-20" />
                    </div>
                    <Skeleton className="h-8 w-full" />
                    <div className="grid grid-cols-2 gap-3">
                      <Skeleton className="h-24 w-full" />
                      <Skeleton className="h-24 w-full" />
                    </div>
                  </CardContent>
                </Card>
              ) : topDuel ? (
                <div className="w-full max-w-sm md:max-w-md lg:max-w-lg [&_.duel-card]:border-0 [&_.duel-card]:shadow-xl md:[&_.duel-card]:scale-90 lg:[&_.duel-card]:scale-105">
                  <DuelCard
                    duel={topDuel}
                    onVote={(id, side) => onVote(id, side)}
                    isAuthenticated={isAuthenticated}
                    isVoting={!!isVotingByDuelId[topDuel.id]}
                    state={(topDuel as unknown as { state?: 'active' | 'locked' | 'resolved' }).state || 'active'}
                    selectedPlayerId={(topDuel as unknown as { selectedPlayerId?: 'player1' | 'player2' | null }).selectedPlayerId || null}
                    playerAPoints={topDuel.player1.gameweek_stats?.total_points}
                    playerBPoints={topDuel.player2.gameweek_stats?.total_points}
                    trackSource="home_hero"
                    isLiveGameweek={isLiveGameweek}
                    solioPredictedA={topDuel.player1.solioProjectedPoints ?? undefined}
                    solioPredictedB={topDuel.player2.solioProjectedPoints ?? undefined}
                    predictionOutcome={topDuel.votes.prediction_outcome}
                    ratingChange={topDuel.votes.rating_change}
                    isDuelOfTheWeek={topDuel.isDuelOfTheWeek}
                    showDOTWBanner={topDuel.isDuelOfTheWeek}
                  />
                </div>
              ) : (
                <Card className="w-full max-w-md shadow-sm">
                  <CardContent className="p-6 flex flex-col items-center text-center space-y-3">
                    <p className="text-muted-foreground">
                      No duels available yet. Check back soon!
                    </p>
                    <Link
                      to="/duels"
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground h-10 px-4"
                    >
                      Go to duels
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Mobile Hero */
        <div className="relative overflow-visible rounded-3xl mx-4 my-8 mb-0">
          <div className="absolute inset-0 bg-gradient-to-br from-background via-secondary/30 to-primary/10 rounded-3xl" />

          {/* Badge - Overlapping top border */}
          {gameweekNumber && (
            <div className="absolute left-1/2 -top-3 -translate-x-1/2 z-10">
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 dark:bg-green-950 border border-green-500/30 rounded-full">
                {isLiveGameweek && (
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                )}
                <span className="text-sm font-medium text-green-700 dark:text-green-300">Live for GW{gameweekNumber}</span>
              </div>
            </div>
          )}

          <div className="relative">
            <div className="px-6 pt-10 pb-3 space-y-4">
              {/* Main Headline */}
              <h1 className="text-2xl font-bold text-foreground leading-tight text-center">
                Pick the right player,{' '}
                <span className="text-primary">every Gameweek</span>
              </h1>

              {/* Subheadline */}
              {/* <p className="text-sm text-muted-foreground text-center">
                Trust the crowd, lead the crowd
              </p> */}
            </div>

            {/* Top Duel - Pinned to bottom */}
            {topDuel && (
              <div>
                <div className="px-6 mb-3">
                  <Separator />
                </div>
                {!topDuel.isDuelOfTheWeek ? (
                  <h2 className="text-base text-muted-foreground text-center px-6 mb-2">
                    Top duel this GW
                  </h2>
                ) : (
                  /* DOTW Banner - rendered here for precise width control */
                  <div className="mx-0 flex items-center justify-center gap-1.5 py-2 bg-gradient-to-r from-gold/20 via-gold/30 to-gold/20 border border-b-0 border-gold/30 rounded-t-xl">
                    <Crown className="h-3.5 w-3.5 text-gold" />
                    <span className="text-xs font-semibold text-gold">Duel of the week</span>
                    <span className="text-xs text-gold/60">·</span>
                    <span className="text-xs font-semibold text-gold">x2 rating points</span>
                  </div>
                )}
                <div className={topDuel.isDuelOfTheWeek
                  ? 'mx-0 [&_.duel-card]:shadow-none [&_.duel-card]:rounded-b-3xl [&_.duel-card]:rounded-t-none [&_.duel-card]:border [&_.duel-card]:border-t-0 [&_.duel-card]:border-gold/30'
                  : '[&_.duel-card]:border-0 [&_.duel-card]:shadow-none [&_.duel-card]:rounded-b-3xl [&_.duel-card]:rounded-t-2xl'
                }>
                  <DuelCard
                    duel={topDuel}
                    onVote={(id, side) => onVote(id, side)}
                    isAuthenticated={isAuthenticated}
                    isVoting={!!isVotingByDuelId[topDuel.id]}
                    state={(topDuel as unknown as { state?: 'active' | 'locked' | 'resolved' }).state || 'active'}
                    selectedPlayerId={(topDuel as unknown as { selectedPlayerId?: 'player1' | 'player2' | null }).selectedPlayerId || null}
                    playerAPoints={topDuel.player1.gameweek_stats?.total_points}
                    playerBPoints={topDuel.player2.gameweek_stats?.total_points}
                    trackSource="home_hero_mobile"
                    isLiveGameweek={isLiveGameweek}
                    solioPredictedA={topDuel.player1.solioProjectedPoints ?? undefined}
                    solioPredictedB={topDuel.player2.solioProjectedPoints ?? undefined}
                    predictionOutcome={topDuel.votes.prediction_outcome}
                    ratingChange={topDuel.votes.rating_change}
                    isDuelOfTheWeek={topDuel.isDuelOfTheWeek}
                    showDOTWBanner={false}
                  />
                </div>
                {/* CTA Button */}
                <div className="flex justify-center px-6 pb-3 mt-2">
                  <Link
                    to="/duels"
                    onClick={() => trackUmamiEvent('hero_all_duels_click', { isMobile: true })}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium bg-primary text-primary-foreground shadow hover:bg-primary/90 h-10 px-4 group"
                  >
                    All Duels
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

